'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { isAuthenticated, signOut, getAccessToken } from '@/services/api/auth'

// Add a helper function to get cookies (since we can't import the private function)
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

interface User {
  user_id?: string
  first_name?: string
  last_name?: string
  email?: string
  picture_url?: string
  permissions?: string[]
}

// Add more specific auth states
type AuthState = 'loading' | 'authenticated' | 'unauthenticated' | 'error'

interface AuthContextType {
  authState: AuthState
  isAuthenticated: boolean
  isLoading: boolean
  logout: () => void
  checkAuthStatus: () => Promise<void>
  user: User | null
  error: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const [authState, setAuthState] = useState<AuthState>('loading')
  const [user, setUser] = useState<User | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Derived states for backward compatibility
  const isLoading = authState === 'loading'
  const isAuthenticatedState = authState === 'authenticated'

  // Function to check if the user is authenticated
  const checkAuthStatus = async () => {
    setAuthState('loading')
    setError(null)
    
    try {
      const token = getAccessToken()
      
      if (token && isAuthenticated()) {
        // Get user data from localStorage
        const userData = localStorage.getItem('user_data')
        
        if (userData) {
          const parsedUserData = JSON.parse(userData)
          
          // Get permissions from sessionStorage first, then fallback to cookies
          let permissionsData = sessionStorage.getItem('user_permissions')
          
          if (!permissionsData) {
            // Fallback to cookies if not in sessionStorage (new tab scenario)
            permissionsData = getCookie('user_permissions')
            
            // If we got permissions from cookies, sync them to sessionStorage
            if (permissionsData) {
              sessionStorage.setItem('user_permissions', permissionsData)
            }
          }
          
          if (permissionsData) {
            parsedUserData.permissions = JSON.parse(permissionsData)
          } else {
            console.warn('No permissions found in sessionStorage or cookies')
          }
          
          setUser(parsedUserData)
          setAuthState('authenticated')
        } else {
          console.warn('No user data found in localStorage')
          setAuthState('unauthenticated')
          setUser(null)
        }
      } else {
        setAuthState('unauthenticated')
        setUser(null)
      }
    } catch (error) {
      console.error('Auth check error:', error)
      setAuthState('error')
      setError(error instanceof Error ? error.message : 'Authentication error')
      setUser(null)
    }
  }

  // Logout function
  const logout = async () => {
    try {
      // Clear OneDrive internal state without signing out of Microsoft
      const oneDriveService = (window as any).oneDriveServiceInstance
      if (oneDriveService && typeof oneDriveService.clearInternalState === 'function') {
        try {
          oneDriveService.clearInternalState()
        } catch (oneDriveError) {
          console.warn('Failed to clear OneDrive internal state:', oneDriveError)
        }
      }
    } catch (error) {
      console.warn('Error during OneDrive state clearing in logout:', error)
    }
    
    // Sign out from main app
    signOut()
    setAuthState('unauthenticated')
    setUser(null)
    setError(null)
    router.push('/login')
  }

  // Check authentication status on mount and when storage changes
  useEffect(() => {
    checkAuthStatus()

    // Listen for storage changes to sync across tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        if (e.newValue) {
          // Token was added/updated, check auth status
          checkAuthStatus()
        } else {
          // Token was removed, sign out
          setAuthState('unauthenticated')
          setUser(null)
        }
      }
    }

    // Remove the focus event listener that was causing frequent re-authentication
    // Only listen for storage changes to sync across tabs
    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const value = {
    authState,
    isAuthenticated: isAuthenticatedState,
    isLoading,
    logout,
    checkAuthStatus,
    user,
    error,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext)
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  
  return context
} 